import { useToast as useVueToastification } from 'vue-toastification';

/**
 * Vue 3 composable for toast notifications using vue-toastification
 * Provides a clean API for showing success, error, warning, and info toasts
 */
export function useToast() {
  const toast = useVueToastification();

  /**
   * Show a success toast notification
   * @param {string} message - The message to display
   * @param {object} options - Additional options for the toast
   * @returns {number} Toast ID
   */
  const success = (message, options = {}) => {
    return toast.success(message, {
      timeout: 5000,
      ...options
    });
  };

  /**
   * Show an error toast notification
   * @param {string} message - The message to display
   * @param {object} options - Additional options for the toast
   * @returns {number} Toast ID
   */
  const error = (message, options = {}) => {
    return toast.error(message, {
      timeout: 7000,
      ...options
    });
  };

  /**
   * Show a warning toast notification
   * @param {string} message - The message to display
   * @param {object} options - Additional options for the toast
   * @returns {number} Toast ID
   */
  const warning = (message, options = {}) => {
    return toast.warning(message, {
      timeout: 6000,
      ...options
    });
  };

  /**
   * Show an info toast notification
   * @param {string} message - The message to display
   * @param {object} options - Additional options for the toast
   * @returns {number} Toast ID
   */
  const info = (message, options = {}) => {
    return toast.info(message, {
      timeout: 5000,
      ...options
    });
  };

  /**
   * Show a default toast notification
   * @param {string} message - The message to display
   * @param {object} options - Additional options for the toast
   * @returns {number} Toast ID
   */
  const show = (message, options = {}) => {
    return toast(message, {
      timeout: 5000,
      ...options
    });
  };

  /**
   * Clear all toasts
   */
  const clear = () => {
    toast.clear();
  };

  /**
   * Dismiss a specific toast by ID
   * @param {number} id - The toast ID to dismiss
   */
  const dismiss = (id) => {
    toast.dismiss(id);
  };

  /**
   * Handle axios error and show appropriate toast
   * @param {object} error - The axios error object
   */
  const handleAxiosError = (error) => {
    if (!error.response) {
      // Network error or request timeout
      error('A network error occurred. Please check your connection and try again.');
      return;
    }

    const { status, data } = error.response;

    switch (status) {
      case 400:
        // Bad request - show validation errors if available
        if (data && Array.isArray(data)) {
          error(data[0] || 'Invalid request. Please check your input.');
        } else if (data && typeof data === 'object' && data.message) {
          error(data.message);
        } else if (data && typeof data === 'object' && data.errors && Array.isArray(data.errors)) {
          error(data.errors[0] || 'Invalid request. Please check your input.');
        } else {
          error('Invalid request. Please check your input.');
        }
        break;
      case 401:
        // Unauthorized - this should be handled by the axios interceptor for redirect
        error('Your session has expired. Please log in again.');
        break;
      case 403:
        error('You do not have permission to perform this action.');
        break;
      case 404:
        error('The requested resource was not found.');
        break;
      case 422:
        // Validation errors
        if (data && data.errors) {
          const firstError = Object.values(data.errors)[0];
          if (Array.isArray(firstError)) {
            error(firstError[0]);
          } else {
            error(firstError || 'Validation failed. Please check your input.');
          }
        } else {
          error('Validation failed. Please check your input.');
        }
        break;
      case 429:
        error('Too many requests. Please wait a moment and try again.');
        break;
      case 500:
        error('A server error occurred. Please try again later.');
        break;
      case 502:
      case 503:
      case 504:
        error('The service is temporarily unavailable. Please try again later.');
        break;
      default:
        error('An unexpected error occurred. Please try again.');
    }
  };

  return {
    success,
    error,
    warning,
    info,
    show,
    clear,
    dismiss,
    handleAxiosError,
    // Expose the raw toast instance for advanced usage
    toast
  };
}
